import electronBridge from './electronBridge';

/**
 * Get media type from various inputs. Optionally return extension and/or base64.
 *
 * @param {string|Blob|File|ArrayBuffer|Uint8Array} input
 *   Accepts: base64 string, data URL, http(s) URL, Blob/File, ArrayBuffer, Uint8Array
 * @param {Object} options
 * @param {boolean} [options.returnExtension=false]  // return "png", "mp4", ...
 * @param {boolean} [options.returnBase64=false]     // include base64 in result
 * @param {boolean} [options.base64AsDataUrl=false]  // when returning base64, prefix with data:mime;base64,
 * @returns {Promise<string|{kind:string, mime:string, ext:string, base64?:string}>}
 */
export async function fetchMediaType(input, {
    returnExtension = false,
    returnBase64 = false,
    base64AsDataUrl = false,
  } = {}) {
    try {
      // 1) Normalize input to bytes (Uint8Array)
      const bytes = await toBytes(input);
  
      // 2) Detect MIME + extension from bytes
      const { mime, ext } = detectFromBytes(bytes) || { mime: 'application/octet-stream', ext: 'bin' };
  
      // 3) Convert to a high-level kind for convenience
      let kind = 'Unknown';
      if (mime.startsWith('image/')) kind = 'Image';
      else if (mime.startsWith('video/')) kind = 'Video';
      else if (mime.startsWith('audio/')) kind = 'Audio';
      else if (mime !== 'application/octet-stream') kind = mime;
  
      if (returnExtension && !returnBase64) {
        return ext;
      }
  
      if (!returnBase64) {
        // Return the same style as your original: a broader kind when possible
        return (kind === 'Unknown') ? mime : kind;
      }
  
      // 4) If asked, produce base64 *here* (short-lived)
      const b64 = bytesToBase64(bytes);
      const base64Out = base64AsDataUrl ? `data:${mime};base64,${b64}` : b64;
  
      return { kind, mime, ext, base64: base64Out };
    } catch (err) {
      console.error('getMediaType error:', err);
      if (returnExtension && !returnBase64) return 'invalid';
      if (!returnBase64) return 'Invalid Input';
      return { kind: 'Invalid', mime: 'application/octet-stream', ext: 'invalid', base64: '' };
    }
  }
  
  /* ---------- Helpers ---------- */
  
  function hasPrefix(str, prefix) {
    return typeof str === 'string' && str.startsWith(prefix);
  }
  
  function isProbablyBase64String(str) {
    // Basic heuristic: not a URL/data:, mostly base64 charset, reasonably long
    if (hasPrefix(str, 'http://') || hasPrefix(str, 'https://') || hasPrefix(str, 'data:')) return false;
    const cleaned = str.trim().replace(/\s/g, '');
    // Avoid false positives on tiny strings
    if (cleaned.length < 16) return false;
    return /^[A-Za-z0-9+/=]+$/.test(cleaned);
  }
  
  function padBase64(cleaned) {
    const padLen = (4 - (cleaned.length % 4)) % 4;
    return cleaned + '='.repeat(padLen);
  }
  
  function atobUniversal(b64) {
    if (typeof atob === 'function') {
      return atob(b64);
    }
    // Node/Electron main or preload
    return Buffer.from(b64, 'base64').toString('binary');
  }
  
  function bytesToBase64(uint8) {
    if (typeof btoa === 'function') {
      let bin = '';
      for (let i = 0; i < uint8.length; i++) bin += String.fromCharCode(uint8[i]);
      return btoa(bin);
    }
    // Node/Electron main or preload
    return Buffer.from(uint8).toString('base64');
  }
  
  async function toBytes(input) {
    // String cases
    if (typeof input === 'string') {
      const s = input.trim();
  
      // data URL?
      if (s.startsWith('data:')) {
        const comma = s.indexOf(',');
        if (comma === -1) throw new Error('Malformed data URL');
        const b64 = s.slice(comma + 1);
        return base64ToBytes(b64);
      }
  
      // http(s) URL?
      if (s.startsWith('http://') || s.startsWith('https://')) {
        const res = await fetch(s);
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
        const buf = await res.arrayBuffer();
        return new Uint8Array(buf);
      }
  
      // raw base64?
      if (isProbablyBase64String(s)) {
        return base64ToBytes(s);
      }
  
      throw new Error('String input is neither URL, data URL, nor probable base64.');
    }
  
    // Blob/File
    if (typeof Blob !== 'undefined' && input instanceof Blob) {
      const buf = await input.arrayBuffer();
      return new Uint8Array(buf);
    }
  
    // ArrayBuffer
    if (input instanceof ArrayBuffer) {
      return new Uint8Array(input);
    }
  
    // Uint8Array (or any TypedArray with .buffer)
    if (input && input.buffer instanceof ArrayBuffer) {
      return new Uint8Array(input.buffer, input.byteOffset || 0, input.byteLength);
    }
  
    throw new Error('Unsupported input type.');
  }
  
  function base64ToBytes(base64String) {
    const cleaned = base64String.trim().replace(/\s/g, '');
    const padded = padBase64(cleaned);
    const binaryStr = atobUniversal(padded);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes;
  }
  
  /**
   * Robust magic-number detector.
   * Handles ambiguous RIFF types (WEBP vs WAV), MP4 ftyp brands, etc.
   */
  function detectFromBytes(bytes) {
    const hex = toHex(bytes, 0, Math.min(bytes.length, 64)); // first 64 bytes
  
    // Quick helpers
    const ascii = (off, s) => strEq(bytes, off, s);
    const u32 = (off) => (off + 4 <= bytes.length)
      ? (bytes[off] | (bytes[off+1] << 8) | (bytes[off+2] << 16) | (bytes[off+3] << 24))
      : 0;
  
    // PNG: 89 50 4E 47 0D 0A 1A 0A
    if (hex.startsWith('89504e470d0a1a0a')) return { mime: 'image/png', ext: 'png' };
  
    // JPEG: FF D8 FF
    if (hex.startsWith('ffd8ff')) return { mime: 'image/jpeg', ext: 'jpg' };
  
    // PDF: %PDF
    if (ascii(0, '%PDF')) return { mime: 'application/pdf', ext: 'pdf' };
  
    // WebM / Matroska: 1A 45 DF A3
    if (hex.startsWith('1a45dfa3')) return { mime: 'video/webm', ext: 'webm' };
  
    // OGG: "OggS"
    if (ascii(0, 'OggS')) {
      // Could be audio/ogg or video/ogg; usually audio
      return { mime: 'audio/ogg', ext: 'ogg' };
    }
  
    // MP3: "ID3" or frame sync 0xFF 0xFB / 0xF3 / 0xF2
    if (ascii(0, 'ID3') || (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0)) {
      return { mime: 'audio/mpeg', ext: 'mp3' };
    }
  
    // MP4 family: "ftyp" at offset 4, with known brands
    // 00 00 00 ?? 66 74 79 70
    if (bytes.length >= 12 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
      // Check major brand for hints (isom, mp42, avc1, etc.)
      return { mime: 'video/mp4', ext: 'mp4' };
    }
  
    // RIFF family: "RIFF" .... then subtype at offset 8
    if (ascii(0, 'RIFF') && bytes.length >= 12) {
      // subtype at 8..11
      if (ascii(8, 'WEBP')) return { mime: 'image/webp', ext: 'webp' };
      if (ascii(8, 'WAVE')) return { mime: 'audio/wav', ext: 'wav' };
      // Could also be AVI, etc. If needed, add more cases.
    }
  
    return null;
  }
  
  function toHex(bytes, start, end) {
    let out = '';
    for (let i = start; i < end; i++) out += bytes[i].toString(16).padStart(2, '0');
    return out;
  }
  
  function strEq(bytes, offset, str) {
    if (offset + str.length > bytes.length) return false;
    for (let i = 0; i < str.length; i++) {
      if (bytes[offset + i] !== str.charCodeAt(i)) return false;
    }
    return true;
  }
  
// If you don't already have this:
function toFileUrl(p) {
  // Windows & POSIX-safe
  if (!p) return p;
  if (/^file:\/\//i.test(p)) return p;
  // Replace backslashes, encode spaces, etc.
  const fixed = p.replace(/\\/g, '/');
  return `safe-file:///${fixed.startsWith('/') ? fixed.slice(1) : fixed}`;
}

/**
 * Get image dimensions from ANY input (URL/base64/dataURL/Blob/Buffer/etc.)
 * Works in browser and Electron.
 * Returns: { width, height }
 */
export async function getImageDimensions(input) {
  // Special case: local file path in Electron renderer (e.g. "C:\foo\bar.png")
  if (
    typeof input === 'string' &&
    (electronBridge?.isElectron) &&
    (input.includes(':\\') || input.startsWith('\\') || input.startsWith('/'))
  ) {
    input = toFileUrl(input);
  }

  // 1) Normalize to bytes using your existing helper
  const bytes = await toBytes(input);

  // 2) Best-effort mime (defaults to png if unknown)
  const detected = detectFromBytes(bytes);
  const mime = detected?.mime?.startsWith('image/')
    ? detected.mime
    : 'image/png';

  // 3) Get dimensions depending on environment
  if (electronBridge?.isElectron) {
    // Electron (renderer or preload): use image-js if you already have it
    // If not imported globally, dynamic import here
    const { Image } = await import('image-js');
    const img = await Image.load(Buffer.from(bytes));
    return { width: img.width, height: img.height };
  } else {
    // Browser: use native APIs (no dependency)
    const blob = new Blob([bytes], { type: mime });

    if ('createImageBitmap' in window) {
      const bmp = await createImageBitmap(blob);
      return { width: bmp.width, height: bmp.height };
    }

    // Fallback via <img>
    const url = URL.createObjectURL(blob);
    try {
      const dims = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = url;
      });
      return dims;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
  